import { User, type UserProfile } from 'oidc-client-ts'

import { getKeycloakUserManager } from '@/auth/keycloakUserManager'
import { LoginServiceUnavailableError } from '@/auth/loginInfrastructureErrors'
import { env } from '@/config/env'
import i18n from '@/translations'

export type KeycloakTokenResponse = {
  access_token: string
  token_type?: string
  expires_in?: number
  refresh_token?: string
  scope?: string
  id_token?: string
  session_state?: string | null
}

function decodeJwtPayload(jwt: string | undefined): UserProfile | undefined {
  if (!jwt || typeof jwt !== 'string') {
    return undefined
  }
  const parts = jwt.split('.')
  if (parts.length < 2) {
    return undefined
  }
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
    const json = atob(padded)
    return JSON.parse(json) as UserProfile
  } catch {
    return undefined
  }
}

export type KeycloakBridgeOptions = {
  /** When true, server skips the first Keycloak grant (use after the browser already tried ROPC). */
  skipKeycloakPrecheck?: boolean
  /** Six-digit TOTP for Keycloak direct grant when the user has OTP configured. */
  otp?: string
}

/**
 * Calls the API bridge (legacy verify + Keycloak sync + tokens). Use `skipKeycloakPrecheck` after a failed direct ROPC.
 */
export async function signinWithPasswordViaBridge(
  email: string,
  password: string,
  options?: KeycloakBridgeOptions
): Promise<User> {
  const apiBase = (env.API_URL as string).replace(/\/$/, '')
  const res = await fetch(`${apiBase}/login/keycloak-legacy-bridge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: email.trim(),
      password,
      ...(options?.skipKeycloakPrecheck === true
        ? { skip_keycloak_precheck: true }
        : {}),
      ...(typeof options?.otp === 'string' &&
      /^\d{6}$/.test(options.otp.replace(/\s+/g, ''))
        ? { otp: options.otp.replace(/\s+/g, '') }
        : {}),
    }),
  })

  if (res.status === 401) {
    throw new Error(
      i18n.t(
        'login.toast.invalidCredentials',
        'Invalid credentials or account unavailable.'
      )
    )
  }

  if (res.status >= 500) {
    throw new LoginServiceUnavailableError()
  }

  if (!res.ok) {
    let msg = 'Login failed.'
    try {
      const err = (await res.json()) as { message?: string }
      if (typeof err.message === 'string' && err.message !== '') {
        msg = err.message
      }
    } catch {
      //
    }
    throw new Error(msg)
  }

  const tokens = (await res.json()) as KeycloakTokenResponse
  if (!tokens.access_token) {
    throw new Error('Login failed.')
  }

  const oidcUser = oidcUserFromKeycloakTokens(tokens)

  const um = getKeycloakUserManager()
  await um.storeUser(oidcUser)
  await um.events.load(oidcUser)

  return oidcUser
}

/** Build an OIDC User from a Keycloak token response (ROPC, bridge, or impersonation). */
export function oidcUserFromKeycloakTokens(
  tokens: KeycloakTokenResponse
): User {
  const profile = (decodeJwtPayload(tokens.id_token) ??
    decodeJwtPayload(tokens.access_token) ?? { sub: '' }) as UserProfile

  const expiresAt =
    typeof tokens.expires_in === 'number'
      ? Math.floor(Date.now() / 1000) + tokens.expires_in
      : undefined

  return new User({
    access_token: tokens.access_token,
    refresh_token: tokens.refresh_token,
    token_type: tokens.token_type ?? 'Bearer',
    scope: tokens.scope,
    id_token: tokens.id_token,
    profile,
    session_state: tokens.session_state ?? null,
    expires_at: expiresAt,
  })
}
